export const SUPPORTED_LOCALES = ["en", "ar", "ru", "ro"] as const;

export type Locale = (typeof SUPPORTED_LOCALES)[number];
export type Direction = "ltr" | "rtl";
export type MonthStyle = "short" | "long";

export type BuyerType = "home" | "office" | "hospitality" | "retail";
export type Familiarity = "regular" | "tried" | "aware" | "new";
export type WaterPreference = "sparkling" | "still" | "both";
export type FormatPreference = "glass" | "pet" | "either";
export type PriceResponse = "yes" | "maybe" | "no";
export type PurchaseFrequency = "weekly" | "monthly" | "occasionally" | "tasting";

export type LocalizedOption<T extends string> = {
  value: T;
  label: string;
};

export type RouteMetadata = {
  title: string;
  description: string;
};

export type MessageCatalog = {
  language: {
    selectorLabel: string;
  };
  brand: {
    homeLabel: string;
  };
  nav: {
    primaryLabel: string;
    mobileLabel: string;
    open: string;
    close: string;
    water: string;
    journey: string;
    journal: string;
    join: string;
  };
  metadata: {
    home: RouteMetadata;
    journal: RouteMetadata;
    article: RouteMetadata;
    research: RouteMetadata;
    privacy: RouteMetadata;
    notFound: RouteMetadata;
  };
  dates: {
    unavailable: string;
    lastUpdated: string;
  };
  hero: {
    title: string;
    lede: string;
    primaryAction: string;
    secondaryAction: string;
    imageAlt: string;
    imageCaption: string;
    independent: string;
    disclosure: string;
  };
  water: {
    introTitle: string;
    introBody: string;
    desalinationFactLabel: string;
    statUnit: string;
    statBody: string;
    dewaReport: string;
    dubaiPathTitle: string;
    dubaiPath: readonly string[];
    borsecPathTitle: string;
    borsecPath: readonly string[];
    originNote: string;
    mineralsTitle: string;
    mineralsBody: string;
    mineralAnalysisLabel: string;
    mineralLabels: Readonly<Record<string, string>>;
    sourceNote: string;
    sourceLink: string;
    imageAlt: string;
  };
  journey: {
    title: string;
    body: string;
    mapLabel: string;
    mapTitle: string;
    stops: readonly {
      name: string;
      detail: string;
    }[];
    routeNote: string;
    signalCount: string;
    signalSummary: string;
    demandTitle: string;
    demandBody: string;
    addSignal: string;
    sourceNote: string;
  };
  validation: {
    title: string;
    lede: string;
    measuresTitle: string;
    measures: readonly string[];
    usefulSignalTitle: string;
    usefulSignalBody: string;
  };
  interestForm: {
    legends: {
      buyer: string;
      familiarity: string;
      water: string;
      format: string;
      priceBefore: string;
      priceBetween: string;
      priceAfter: string;
      frequency: string;
    };
    options: {
      buyer: readonly LocalizedOption<BuyerType>[];
      familiarity: readonly LocalizedOption<Familiarity>[];
      water: readonly LocalizedOption<WaterPreference>[];
      format: readonly LocalizedOption<FormatPreference>[];
      price: readonly LocalizedOption<PriceResponse>[];
      frequency: readonly LocalizedOption<PurchaseFrequency>[];
    };
    priceQualifier: string;
    email: string;
    emailPlaceholder: string;
    area: string;
    optional: string;
    areaPlaceholder: string;
    contactConsent: string;
    marketingConsent: string;
    saving: string;
    submit: string;
    footnote: string;
    successTitle: string;
    successBody: string;
    successAction: string;
    errors: {
      verificationExpired: string;
      verificationLoad: string;
      verificationMisconfigured: string;
      emailRequired: string;
      emailInvalid: string;
      consentRequired: string;
      verificationRequired: string;
      verificationFailed: string;
      saveFailed: string;
      network: string;
      invalidRequest: string;
      conflict: string;
    };
  };
  faq: {
    title: string;
    items: readonly {
      question: string;
      answer: string;
    }[];
  };
  finalCta: {
    title: string;
    body: string;
    join: string;
    trade: string;
  };
  footer: {
    navigationLabel: string;
    research: string;
    journal: string;
    privacy: string;
    contact: string;
    disclosure: string;
    location: string;
  };
  research: {
    title: string;
    intro: string;
    hypothesisLabel: string;
    hypothesisBody: string;
    gatesTitle: string;
    gatesBody: string;
    metrics: readonly {
      value: string;
      label: string;
      note: string;
    }[];
    testTitle: string;
    tests: readonly {
      title: string;
      body: string;
    }[];
    guardrailsTitle: string;
    guardrails: readonly string[];
    sourcesTitle: string;
    sourcesBody: string;
    sources: readonly {
      label: string;
      href: string;
    }[];
  };
  privacy: {
    title: string;
    lede: string;
    collectTitle: string;
    collectBody: string;
    useTitle: string;
    useBody: string;
    storageTitle: string;
    storageBody: string;
    retentionTitle: string;
    retentionBody: string;
    statusTitle: string;
    statusBody: string;
  };
  notFound: {
    eyebrow: string;
    title: string;
    body: string;
    back: string;
  };
  blog: {
    common: {
      minuteShort: string;
      minutes: (count: number) => string;
      readNote: string;
    };
    preview: {
      title: string;
      intro: string;
      readAll: string;
      empty: string;
      unavailable: string;
    };
    index: {
      title: string;
      intro: string;
      ruleLabel: string;
      ruleBody: string;
      read: string;
    };
    article: {
      allNotes: string;
      sourcesTitle: string;
      sourcesIntro: string;
      notFoundEyebrow: string;
      notFoundTitle: string;
      notFoundBody: string;
      backToJournal: string;
      outageEyebrow: string;
      outageTitle: string;
      outageBody: string;
      tryAgain: string;
      loading: string;
      visualsLabel: string;
    };
    categories: Readonly<Record<string, string>>;
  };
};
